import React from "react";
import { statusStyles, avatarColors } from "./data";
import { SearchIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, ChevronRightIcon as RowChevron } from "./icons";

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  liveIds,
  joinedIds,
  unreadIds,
  search,
  onSearchChange,
  page = 1,
  totalPages = 1,
  total = 0,
  limit = 10,
  loading = false,
  onPageChange = () => {},
}) {
  // Taken from the page size rather than the row count: the last page is
  // short, and deriving the offset from it would misreport every range.
  const firstRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(firstRow + conversations.length - 1, total);

  // A window around the current page: every page button is unusable once a
  // busy account has fifty of them.
  const windowStart = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pageNumbers = Array.from(
    { length: Math.min(5, totalPages) },
    (_, i) => windowStart + i,
  ).filter((number) => number >= 1 && number <= totalPages);

  return (
    <section className="dash-card conv-list-card">
      <div className="conv-search-row">
        <label className="onboarding__input-wrap conv-search-input">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>
      </div>

      <ul className="conv-list">
        {conversations.map((c) => {
          // A status the API sends that is not one of the known labels has no
          // palette entry, so this must not be indexed blind.
          const style = statusStyles[c.status] || {};
          return (
            <li key={c.id}>
              <button
                type="button"
                className={`conv-list-item${selectedId === c.id ? " is-selected" : ""}`}
                onClick={() => onSelect(c.id)}
              >
                <span className="conv-avatar-wrap">
                  <span className="conv-avatar" style={{ background: avatarColors[c.color] }}>
                    {c.initials}
                  </span>
                  {c.online && <span className="conv-online-dot" />}
                </span>

                <span className="conv-item-body">
                  <span className="conv-item-top">
                    <strong>{c.name}</strong>
                    {unreadIds?.has(c.id) && (
                      <span className="conv-unread-dot" aria-label="New activity" />
                    )}
                    <span className="conv-item-time">{c.time}</span>
                  </span>
                  <span className="conv-item-preview">{c.preview}</span>
                  <span className="conv-item-bottom">
                    <span className="conv-item-tags">
                      {/* Held by an owner right now - either this session or
                          a teammate's, which the socket reports either way. */}
                      {liveIds?.has(c.id) && (
                        <span className="conv-tag-chip conv-tag-chip--live">
                          <span className="conv-live-dot" />
                          {joinedIds?.has(c.id) ? "You're live" : "Live"}
                        </span>
                      )}
                      {/* Only training is called out: a widget conversation is
                          the norm and labelling every row would be noise. */}
                      {c.source === "training" && (
                        <span className="conv-tag-chip conv-tag-chip--training">
                          Training
                        </span>
                      )}
                      {(c.tags || []).slice(0, 3).map((tag) => (
                        <span className="conv-tag-chip" key={tag}>
                          {tag}
                        </span>
                      ))}
                    </span>
                    {c.status && (
                      <span
                        className="conv-status-badge"
                        style={{ background: style.bg, color: style.color }}
                      >
                        {c.status}
                      </span>
                    )}
                  </span>
                </span>

                <RowChevron />
              </button>
            </li>
          );
        })}

        {conversations.length === 0 && <li className="conv-empty">No conversations match this filter.</li>}
      </ul>

      <div className="conv-pagination">
        <span>
          {total > 0
            ? `Showing ${firstRow} to ${lastRow} of ${total} conversations`
            : `${conversations.length} conversations`}
        </span>
        <div className="conv-pagination-controls">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1 || loading}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </button>

          {pageNumbers.map((number) => (
            <button
              type="button"
              key={number}
              className={number === page ? "is-active" : ""}
              onClick={() => onPageChange(number)}
              disabled={loading}
            >
              {number}
            </button>
          ))}

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages || loading}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </section>
  );
}
